import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./lib/auth";
import { ConfigProvider } from "./lib/config";
import { trackPage } from "./lib/analytics";
import RouteMeta from "./components/RouteMeta";
import ImpersonationBanner from "./components/ImpersonationBanner";
import Landing from "./pages/Landing";
import Waitlist from "./pages/Waitlist";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import NewProject from "./pages/NewProject";
import ProjectDetail from "./pages/ProjectDetail";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import BillingSuccess from "./pages/BillingSuccess";
import PublicShare from "./pages/PublicShare";
import Teams from "./pages/Teams";
import Verify from "./pages/Verify";
import ForgotPassword from "./pages/ForgotPassword";
import MagicLogin from "./pages/MagicLogin";
import Features from "./pages/Features";
import FAQ from "./pages/FAQ";
import { Privacy, Terms, Security, Contact } from "./pages/Legal";
import UseCases from "./pages/UseCases";
import Gallery from "./pages/Gallery";
import TemplatesPage from "./pages/TemplatesPage";
import LaunchKit from "./pages/LaunchKit";
import Storyboard from "./pages/Storyboard";
import Admin from "./pages/Admin";
import NotFound from "./pages/NotFound";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-muted uppercase tracking-widest text-xs">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPage(location.pathname);
  }, [location.pathname]);
  return null;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/verify/:token" element={<Verify />} />
      <Route path="/magic/:token" element={<MagicLogin />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/features" element={<Features />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/security" element={<Security />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/use-cases" element={<UseCases />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/templates" element={<TemplatesPage />} />
      <Route path="/share/:token" element={<PublicShare />} />
      <Route path="/billing/success" element={<Protected><BillingSuccess /></Protected>} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/new" element={<Protected><NewProject /></Protected>} />
      <Route path="/project/:id" element={<Protected><ProjectDetail /></Protected>} />
      <Route path="/project/:id/storyboard" element={<Protected><Storyboard /></Protected>} />
      <Route path="/project/:id/launch-kit" element={<Protected><LaunchKit /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/teams" element={<Protected><Teams /></Protected>} />
      <Route path="/admin" element={<Protected><Admin /></Protected>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ConfigProvider>
      <AuthProvider>
        <BrowserRouter>
          <PageTracker />
          <RouteMeta />
          <ImpersonationBanner />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </ConfigProvider>
  );
}
